import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    MatchupWrapper,
    MatchupPlayerWrapper,
    PlayerDataWrapper,
    PlayerNameWrapper,
    PlayerDetailsWrapper,
    PlayerDataDivider,
    PlayerSpotInfo,
    PlayerSpotNameInfo,
    CountWrapper,
    InningStateWrapper
} from './Summary.style';
import Headshot from '@mlb/react-patterns/v4/components/Headshot/headshot';
import buildPlayerUrl from '@gameday/utils/buildPlayerUrl';
import useTracking from '@gameday/utils/hooks/useTracking';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import { resourcePath } from '@gameday/utils/resource-path';
import { useSelector } from 'react-redux';
import { getContextData } from '@gameday/utils/selectors/getContextData';
import Basepaths from '@mlb/react-patterns/v4/components/Basepaths/basepaths';
import Outs from '@mlb/react-patterns/v4/components/Outs/outs';

const DataDivider = () => {
    return (<PlayerDataDivider>|</PlayerDataDivider>);
};

const countCheck = (passedCount) => {
    return typeof passedCount !== 'undefined' && passedCount !== null;
};

const HeadshotComponent = (headshotType, id, boxscoreName) => (
    <Headshot
        type={headshotType}
        playerId={id}
        alt={`${boxscoreName} headshot`}
        zoomScale={1}
        ratio='1:1'
    />
);

const Pitcher = ({
    data,
    language,
    onClick,
    isMobile
}) => {
    const {
        jerseyNumber,
        person: {
            id,
            boxscoreName,
            nameSlug,
            pitchHand: {
                code
            } = {}
        } = {}
    } = data;

    const getPlayerContextData = useSelector(getContextData);
    const { headshotType, disablePlayerLinks, playerPageContext } = getPlayerContextData(id);
    const url = buildPlayerUrl({ nameSlug, language, playerPageContext });
    return (<MatchupPlayerWrapper>
        {!disablePlayerLinks
            ? (
                <Link
                    url={buildPlayerUrl({ nameSlug, language, playerPageContext })}
                    target='_blank'
                    ariaLabel={boxscoreName}
                    handlers={ { onClick } }
                    dataAttr={{ 'data-playerid': id, 'data-url': url }}
                >
                    {HeadshotComponent(headshotType, id, boxscoreName)}
                </Link>
            )
            : (
                HeadshotComponent(headshotType, id, boxscoreName)
            )}

        <PlayerDataWrapper>
            <PlayerSpotNameInfo $isMobile={isMobile}>
                <PlayerNameWrapper $isMobile={isMobile}>
                    {!disablePlayerLinks
                        ? (
                            <Link url={buildPlayerUrl({ nameSlug, language, playerPageContext })} target='_blank' ariaLabel={boxscoreName} handlers={{ onClick }} dataAttr={{ 'data-playerid': id, 'data-context': playerPageContext }}>
                                {boxscoreName}
                            </Link>
                        )
                        : (
                            boxscoreName
                        )}
                </PlayerNameWrapper>
            </PlayerSpotNameInfo>
            <PlayerSpotInfo $isMobile={isMobile}>
                <PlayerDetailsWrapper>
                    {code}HP
                </PlayerDetailsWrapper>
                <DataDivider/>
                <PlayerDetailsWrapper>
                    {jerseyNumber && `#${jerseyNumber}`}
                </PlayerDetailsWrapper>
            </PlayerSpotInfo>
        </PlayerDataWrapper>
    </MatchupPlayerWrapper>);
};

const Batter = ({
    data,
    language,
    onClick,
    isMobile
}) => {
    const {
        jerseyNumber,
        position: {
            abbreviation
        } = {},
        person: {
            id,
            boxscoreName,
            nameSlug,
            batSide: {
                code
            } = {}
        } = {}
    } = data;
    const getPlayerContextData = useSelector(getContextData);
    const { headshotType, disablePlayerLinks, playerPageContext } = getPlayerContextData(id);
    const url = buildPlayerUrl({ nameSlug, language, playerPageContext });
    return (<MatchupPlayerWrapper $batter={true}>
        {!disablePlayerLinks
            ? (
                <Link
                    url={url}
                    target='_blank'
                    ariaLabel={boxscoreName}
                    handlers={ { onClick } }
                    dataAttr={{ 'data-playerid': id, 'data-url': url }}
                >
                    {HeadshotComponent(headshotType, id, boxscoreName)}
                </Link>
            )
            : (
                HeadshotComponent(headshotType, id, boxscoreName)
            )}
        <PlayerDataWrapper $batter={true}>
            <PlayerSpotNameInfo $isMobile={isMobile} $batter={true}>
                <PlayerNameWrapper $isMobile={isMobile}>
                    {!disablePlayerLinks
                        ? (
                            <Link url={buildPlayerUrl({ nameSlug, language, playerPageContext })} target='_blank' ariaLabel={boxscoreName} handlers={{ onClick }} dataAttr={{ 'data-playerid': id, 'data-context': playerPageContext }}>
                                {boxscoreName}
                            </Link>
                        )
                        : (
                            boxscoreName
                        )}
                </PlayerNameWrapper>
            </PlayerSpotNameInfo>
            <PlayerSpotInfo $isMobile={isMobile}>
                <PlayerDetailsWrapper>
                    {abbreviation}
                </PlayerDetailsWrapper>
                <DataDivider/>
                <PlayerDetailsWrapper>
                    {jerseyNumber && `#${jerseyNumber}`}
                </PlayerDetailsWrapper>
                <DataDivider/>
                <PlayerDetailsWrapper>
                    {code}
                </PlayerDetailsWrapper>
            </PlayerSpotInfo>
        </PlayerDataWrapper>
    </MatchupPlayerWrapper>);
};

const Matchup = ({
    matchup,
    away,
    home,
    isMobile,
    language,
    deviceInteraction,
    offense = {},
    count = {}
}) => {
    const {
        batter = {},
        pitcher = {}
    } = matchup;

    const pitcherId = pitcher.id;
    const batterId = batter.id;

    if (!pitcherId || !batterId) {
        return null;
    }

    const awayPlayerData = away?.playerById || [];
    const homePlayerData = home?.playerById || [];

    const pitcherData = awayPlayerData[pitcherId] || homePlayerData[pitcherId];
    const batterData = awayPlayerData[batterId] || homePlayerData[batterId];

    const batterTeamId = awayPlayerData[batterId] ? away?.team?.id : home?.team?.id;
    const pitcherTeamId = awayPlayerData[pitcherId] ? away?.team?.id : home?.team?.id;

    const {
        balls,
        strikes,
        outs
    } = count || {};

    const Tracking = useTracking();

    const onClick = useCallback((e) => {
        const text = e?.target.innerText;
        const playerId = e?.currentTarget?.dataset?.playerid;
        const url = e?.currentTarget?.dataset?.url;

        const cfg = {
            componentName: ['AtBat Detail', 'Match up'],
            text,
            activity: 'Link Click'
        };

        Tracking.track.track_click(cfg);

        if (deviceInteraction.playercard({ title: 'Player Card', url, pid: playerId })) {
            e.preventDefault();
        }
    }, [Tracking, deviceInteraction, resourcePath]);

    return (
        <MatchupWrapper
            isMobile={isMobile}
        >
            <Pitcher data={pitcherData} teamId={pitcherTeamId} language={language} onClick={onClick} isMobile={isMobile} />
            <InningStateWrapper>
                <Basepaths {...offense} width={37} rounded={false} filledColor={'var(--gameday-basepath-color)'} strokeColor={'var(--gameday-basepath-color)'} emptyColor='transparent' />
                <CountWrapper>{(countCheck(balls)) && (countCheck(strikes)) ? `${balls} - ${strikes}` : ''}</CountWrapper>
                <Outs outCount={outs} width={37} outPadding={2.5} outSize={2} outsActiveColor={'var(--gameday-outs-color)'} outsInactiveColor='transparent' stroke />
            </InningStateWrapper>
            <Batter data={batterData} teamId={batterTeamId} language={language} onClick={onClick} isMobile={isMobile} />
        </MatchupWrapper>
    );
};

Matchup.propTypes = {
    matchup: PropTypes.object,
    away: PropTypes.object,
    home: PropTypes.object,
    isMobile: PropTypes.bool
};

export default Matchup;
